import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueueStore } from 'Store/Queue'
import Socket from 'Configuration/Socket'
import Swal from 'sweetalert2'

const ViewModel = () => {
  const [searchParams] = useSearchParams()
  const secret = searchParams.get('secret')
  const [socket, setSocket] = useState<any>(null)
  const [info, setInfo] = useState<any>({})
  const [manualCall, setManualCall] = useState<boolean>(false)
  const [isShowWaiting, setIsShowWaiting] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [patientNameText, setPatientNameText] = useState<string>('')

  const [searchPatientText, setSearchPatientText] = useState<string>('')
  const [selectedSearchPatientId, setSelectedSearchPatientId] =
    useState<string>('')
  const { searchPatientData, getSearchPatient, assignPatientToQueue } =
    useQueueStore(state => ({
      searchPatientData: state.searchPatient,
      getSearchPatient: state.getSearchPatient,
      assignPatientToQueue: state.assignPatientToQueue
    }))
  const selectedPatient = useMemo(() => {
    return searchPatientData.find(d => d.uuid === selectedSearchPatientId) ?? {}
  }, [searchPatientData, selectedSearchPatientId])
  const canSubmitSearchPatient = useMemo(() => {
    return (
      selectedSearchPatientId.length !== 0 &&
      Object.keys(selectedPatient).length > 0
    )
  }, [selectedSearchPatientId, selectedPatient])
  const hasPatient = useMemo(() => {
    return (
      info?.queue?.patientId !== null &&
      info?.queue?.patientId !== '' &&
      info?.queue?.patientId !== undefined
    )
  }, [info])

  const handleInfo = (data: any) => {
    setInfo(data)
    setIsShowWaiting(false)

    if (data?.queue?.status === '20' && !manualCall) {
      Swal.fire({
        icon: 'success',
        title: 'ถึงคิวของคุณแล้ว!',
        showConfirmButton: true,
        confirmButtonText: 'ตกลง'
      })
    }
  }

  const handleError = (data: any) => {
    switch (data) {
      // Queue not found
      case 'error1': {
        Swal.fire({
          icon: 'error',
          text: 'ไม่พบคิวของคุณ',
          showConfirmButton: true,
          confirmButtonText: 'ตกลง'
        })
        break
      }
      // Queue found but not today
      case 'error2': {
        Swal.fire({
          icon: 'warning',
          text: 'คิวของคุณไม่ได้จองในวันนี้',
          showConfirmButton: true,
          confirmButtonText: 'ตกลง'
        })
        break
      }
      default: {
        break
      }
    }
  }

  const handleCall = () => {
    Swal.fire({
      icon: 'success',
      title: 'ถึงคิวของคุณแล้ว!',
      showConfirmButton: true,
      confirmButtonText: 'ตกลง'
    })

    if (socket) {
      socket.emit('info', handleInfo)
      setManualCall(true)
    }
  }

  const handleSkip = () => {
    Swal.fire({
      icon: 'info',
      title: 'คุณโดนข้ามคิวแล้ว',
      text: 'หากต้องการคิวใหม่ โปรดติดต่อเจ้าหน้าที่',
      showConfirmButton: true,
      confirmButtonText: 'ตกลง'
    })

    if (socket) {
      socket.emit('info', handleInfo)
      setManualCall(true)
    }
  }

  const handleCancel = () => {
    Swal.fire({
      icon: 'info',
      text: 'คุณโดนยกเลิกคิวแล้ว',
      showConfirmButton: true,
      confirmButtonText: 'ตกลง'
    })

    if (socket) {
      socket.emit('info', handleInfo)
      setManualCall(true)
    }
  }

  const handleWhistle = () => {
    Swal.fire({
      icon: 'info',
      title: 'กรุณามาที่เคาน์เตอร์',
      showConfirmButton: true,
      confirmButtonText: 'ตกลง'
    })
  }

  const onSubmitSearchPatient = () => {
    if (secret) {
      if (selectedSearchPatientId.length === 0) {
        Swal.fire({
          icon: 'warning',
          text: 'กรุณากรอกชื่อ/นามสกุลเพื่อค้นหา',
          showConfirmButton: true,
          confirmButtonText: 'ตกลง'
        })
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'ยืนยันข้อมูลคนไข้',
          text: 'กรุณาตรวจสอบชื่อและนามสกุลให้ถูกต้องครบถ้วน',
          showCancelButton: true,
          showConfirmButton: true,
          cancelButtonText: 'ยกเลิก',
          confirmButtonText: 'ยืนยัน'
        }).then(result => {
          if (result.isConfirmed) {
            assignPatientToQueue(secret, {
              uuid: selectedSearchPatientId
            }).then(({ success, data }) => {
              if (!success) {
                Swal.fire({
                  icon: 'error',
                  text: data,
                  showConfirmButton: true,
                  confirmButtonText: 'ตกลง'
                })
                return
              } else {
                Swal.fire({
                  icon: 'success',
                  title: 'บันทึกข้อมูลสำเร็จ',
                  text: 'เจ้าหน้าที่กำลังจัดเตรียมเอกสารของท่าน',
                  showConfirmButton: true,
                  confirmButtonText: 'ตกลง'
                })
                setSearchPatientText('')
                setSelectedSearchPatientId('')
                socket.emit('info', handleInfo)
              }
            })
          }
        })
      }
    }
  }

  const onSubmitPatientName = () => {
    if (secret) {
      if (patientNameText.length === 0) {
        Swal.fire({
          icon: 'warning',
          text: 'กรุณากรอกชื่อ/นามสกุลอย่างน้อย 3 ตัวอักษร',
          showConfirmButton: true,
          confirmButtonText: 'ตกลง'
        })
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'ยืนยันข้อมูลคนไข้',
          text: 'กรุณาตรวจสอบชื่อและนามสกุลให้ถูกต้องครบถ้วน',
          showCancelButton: true,
          showConfirmButton: true,
          cancelButtonText: 'ยกเลิก',
          confirmButtonText: 'ยืนยัน'
        }).then(result => {
          if (result.isConfirmed) {
            setIsSubmitting(true)

            assignPatientToQueue(secret, {
              patientName: patientNameText
            })
              .then(({ success, data }) => {
                if (!success) {
                  Swal.fire({
                    icon: 'error',
                    text: data,
                    showConfirmButton: true,
                    confirmButtonText: 'ปิด'
                  })
                  return
                } else {
                  Swal.fire({
                    icon: 'success',
                    title: 'บันทึกข้อมูลสำเร็จ',
                    text: 'เจ้าหน้าที่กำลังจัดเตรียมเอกสารของท่าน',
                    showConfirmButton: true,
                    confirmButtonText: 'ตกลง'
                  })
                  setPatientNameText('')
                  socket.emit('info', handleInfo)
                }
              })
              .finally(() => {
                setIsSubmitting(false)
              })
          }
        })
      }
    }
  }

  const searchPatientPromiseOptions = (
    value: string
  ): Promise<{ label: string; value: string }[]> => {
    if (!secret || value.length === 0) return Promise.resolve([])

    return new Promise(resolve => {
      getSearchPatient(secret, { search: value }).then(({ data, success }) => {
        if (success) {
          resolve(
            data.map((p: any) => ({
              label: `${p?.firstName} ${p?.lastName}`,
              value: p?.uuid
            }))
          )
        }
      })
    })
  }

  useEffect(() => {
    if (!secret) {
      setTimeout(() => {
        setIsShowWaiting(false)
      }, 1200)
      return
    }
    const socket = Socket(secret)
    setSocket(socket)
    return () => {
      socket.disconnect()
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!socket) return
    socket.on('info', handleInfo)
    socket.on('error', handleError)
    socket.on('call', handleCall)
    socket.on('skip', handleSkip)
    socket.on('cancel', handleCancel)
    socket.on('whistle', handleWhistle)

    return () => {
      socket.off('info')
      socket.off('error')
      socket.off('call')
      socket.off('skip')
      socket.off('cancel')
      socket.off('whistle')
    }
    // eslint-disable-next-line
  }, [socket])

  return {
    hasPatient,
    canSubmitSearchPatient,
    isShowWaiting,
    isSubmitting,
    info,
    patientNameText,
    setPatientNameText,
    searchPatientPromiseOptions,
    searchPatientText,
    setSearchPatientText,
    setSelectedSearchPatientId,
    onSubmitSearchPatient,
    onSubmitPatientName
  }
}

export default ViewModel
